import React from 'react';
import { useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button } from 'primereact/button';

import FormInputText from '../../components/form/FormInputText';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';

type EquifaxVoieFormValues = {
  signOnRequestUserId: string;
  signOnRequestUserPassword: string;
};

interface EditEquifaxVoieIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditEquifaxVoieIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditEquifaxVoieIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let signOnRequestUserId = '';
  let signOnRequestUserPassword = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    signOnRequestUserId = data.signOnRequestUserId || '';
    signOnRequestUserPassword = data.signOnRequestUserPassword || '';
  }

  const validationSchema = yup.object().shape({
    signOnRequestUserId: yup.string().required('User ID is required'),
    signOnRequestUserPassword: yup.string().required('Password is required'),
  });

  const { control, handleSubmit, formState } = useForm<EquifaxVoieFormValues>({
    defaultValues: {
      signOnRequestUserId: signOnRequestUserId,
      signOnRequestUserPassword: signOnRequestUserPassword,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  const onSubmit = (data: EquifaxVoieFormValues) => {
    console.log('Form validated successfully:', data);

    if (onSave) {
      let settings: IntegrationSettings;

      if (isEditMode && integrationSettings) {
        settings = {
          ...integrationSettings,
          environment: environment,
          integrationData: JSON.stringify({
            signOnRequestUserId: data.signOnRequestUserId,
            signOnRequestUserPassword: data.signOnRequestUserPassword,
          }),
        };
      } else {
        settings = {
          name: 'Equifax VOIE Integration',
          vendor: IntegrationVendor.Equifax,
          status: IntegrationStatus.Active,
          environment: environment,
          integrationData: JSON.stringify({
            signOnRequestUserId: data.signOnRequestUserId,
            signOnRequestUserPassword: data.signOnRequestUserPassword,
          }),
        };
      }

      onSave(settings);
    }
  };

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Equifax VOIE Settings' : 'Equifax VOIE Settings'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled">
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="signOnRequestUserId"
            label="Sign-On Request User ID *"
            placeholder="Enter your Equifax User ID"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="signOnRequestUserPassword"
            label="Sign-On Request User Password *"
            placeholder="Enter your Equifax Password"
            type="password"
          />
        </div>

        <div className="flex justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
